import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from "@capacitor/core";

// Conditionally import JStyle only on native platforms
let JStyle = null;
if (Capacitor.isNativePlatform()) {
  const JStyleModule = require("../../JStylePlugin");
  JStyle = JStyleModule.default;
}

// Icons for heart metrics
const HeartRateIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);

const HRVIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>
);

const BloodOxygenIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
    <line x1="9" y1="9" x2="9.01" y2="9"></line>
    <line x1="15" y1="9" x2="15.01" y2="9"></line>
  </svg>
);

const VO2MaxIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const Heart = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementData, setMeasurementData] = useState(null);
  const [currentMetric, setCurrentMetric] = useState('');
  const [status, setStatus] = useState('');
  const [isConnected, setConnected] = useState(false);

  // Add listener for real-time measurement data
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Web platform - device features not available");
      return;
    }

    // Add listener for real-time measurement data
    JStyle.addListener("rtMeasureData", handleMeasurementData);
    JStyle.addListener("connectionResult", handleConnectionEvent); //auto connect event
    JStyle.addListener("disconnectionResult", handleDisconnectionEvent); //auto disconnect event

    var jstyle_address = localStorage.getItem("jstyle_address");
    if(jstyle_address==null || jstyle_address=='' || jstyle_address==undefined) {
      JStyle.checkPermission().then((result) => {
        console.log(result.permission);
        if (result.permission === "granted") {
        } else {
          setStatus("App Requires Bluetooth Permission");
          JStyle.requestPermission();
        }
      });
    }
    else {
    JStyle.isConnected().then((result) => {
        if (result.connected === true) {
          setConnected(true);
        } else {
          JStyle.connect({ deviceId: jstyle_address });
        }
      });
    }
    
    return () => {
      if (JStyle) {
        JStyle.removeListener("rtMeasureData");
        JStyle.removeListener("connectionResult");
        JStyle.removeListener("disconnectionResult");
        JStyle.disconnect();
      }
    };
  }, []);

  const handleConnectionEvent = (deviceInfo) => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      return;
    }
    setStatus("connected");
    localStorage.setItem("jstyle_address", deviceInfo.address);
    setConnected(true);
  };
  
  const handleDisconnectionEvent = (deviceInfo) => {
    setStatus("disconnected");
    setConnected(false);
  };

  const handleMeasurementData = (measurementResult) => {
    console.log("Real-time measurement data received:", measurementResult);
    setIsMeasuring(false);
    
    // Parse the measurement data
    try {
      const { heart, hrv, blood_oxygen, measurement } = measurementResult;
      
      // Create a structured measurement data object with specific values
      const data = {
        heart: heart && heart !== 0 ? heart : null,
        hrv: hrv && hrv !== 0 ? hrv : null,
        blood_oxygen: blood_oxygen && blood_oxygen !== 0 ? blood_oxygen : null,
        status: "Success",
        timestamp: new Date().toLocaleTimeString(),
        rawData: measurementResult
      };
      
      setMeasurementData(data);
    } catch (error) {
      console.error("Error parsing measurement data:", error);
      setMeasurementData({
        heart: null,
        hrv: null,
        blood_oxygen: null,
        status: "Failed",
        timestamp: new Date().toLocaleTimeString(),
        rawData: measurementResult
      });
    }
  };

  // Generate random data for different metrics
  const generateHeartRateData = () => {
    const data = {};
    const startDate = new Date('2023-09-01');
    const endDate = new Date('2025-07-31');
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateKey = date.toISOString().split('T')[0];
      const dayData = [];
      
      for (let hour = 0; hour < 24; hour++) {
        if (Math.random() > 0.3) {
          const min = 60 + Math.random() * 40; // 60-100 bpm
          const max = min + Math.random() * 20; // 20 bpm range
          dayData.push({
            hour: hour,
            min: Math.round(min),
            max: Math.round(max),
            avg: Math.round((min + max) / 2)
          });
        }
      }
      
      if (dayData.length > 0) {
        data[dateKey] = dayData;
      }
    }
    
    return data;
  };

  const generateHRVData = () => {
    const data = {};
    const startDate = new Date('2023-09-01');
    const endDate = new Date('2025-07-31');
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateKey = date.toISOString().split('T')[0];
      const dayData = [];
      
      for (let hour = 0; hour < 24; hour++) {
        if (Math.random() > 0.3) {
          const min = 20 + Math.random() * 60; // 20-80 ms
          const max = min + Math.random() * 30; // 30 ms range
          dayData.push({
            hour: hour,
            min: Math.round(min),
            max: Math.round(max),
            avg: Math.round((min + max) / 2)
          });
        }
      }
      
      if (dayData.length > 0) {
        data[dateKey] = dayData;
      }
    }
    
    return data;
  };

  const generateBloodOxygenData = () => {
    const data = {};
    const startDate = new Date('2023-09-01');
    const endDate = new Date('2025-07-31');
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateKey = date.toISOString().split('T')[0];
      const dayData = [];
      
      for (let hour = 0; hour < 24; hour++) {
        if (Math.random() > 0.3) {
          const min = 95 + Math.random() * 5; // 95-100%
          const max = min + Math.random() * 2; // 2% range
          dayData.push({
            hour: hour,
            min: Math.round(min * 10) / 10,
            max: Math.round(max * 10) / 10,
            avg: Math.round(((min + max) / 2) * 10) / 10
          });
        }
      }
      
      if (dayData.length > 0) {
        data[dateKey] = dayData;
      }
    }
    
    return data;
  };

  const generateVO2MaxData = () => {
    const data = {};
    const startDate = new Date('2023-09-01');
    const endDate = new Date('2025-07-31');
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateKey = date.toISOString().split('T')[0];
      const dayData = [];
      
      for (let hour = 0; hour < 24; hour++) {
        if (Math.random() > 0.3) {
          const min = 30 + Math.random() * 20; // 30-50 ml/kg/min
          const max = min + Math.random() * 8; // 8 ml/kg/min range
          dayData.push({
            hour: hour,
            min: Math.round(min * 10) / 10,
            max: Math.round(max * 10) / 10,
            avg: Math.round(((min + max) / 2) * 10) / 10
          });
        }
      }
      
      if (dayData.length > 0) {
        data[dateKey] = dayData;
      }
    }
    
    return data;
  };

  const handleCardClick = (metricType) => {
    let data;
    let dataType;
    
    switch(metricType) {
      case 'heartRate':
        data = generateHeartRateData();
        dataType = 'heartrate';
        break;
      case 'hrv':
        data = generateHRVData();
        dataType = 'hrv';
        break;
      case 'bloodOxygen':
        data = generateBloodOxygenData();
        dataType = 'spo2';
        break;
      case 'vo2max':
        data = generateVO2MaxData();
        dataType = 'vo2max';
        break;
      default:
        return;
    }
    
    // Navigate to VisualizeWellnessData with data
    navigate('/landing/tab3/VisualizeWellnessData', {
      state: {
        dataType: dataType,
        data: data
      }
    });
  };

  const handleMeasure = (metricType) => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device measurement not available on web platform");
      return;
    }

    setCurrentMetric(metricType);
    setIsDialogOpen(true);
    setIsMeasuring(true);
    setMeasurementData(null);
    if(!isConnected) return;

    setStatus("Starting measurement...");

    // Map metric types to JStyle measurement types
    let measurementType = "heart";
    switch(metricType) {
      case 'heartRate':
        measurementType = "heart";
        break;
      case 'hrv':
        measurementType = "heart"; // HRV is measured with heart rate
        break;
      case 'bloodOxygen':
        measurementType = "spO2";
        break;
      default:
        setStatus("Unsupported measurement type");
        setIsMeasuring(false);
        return;
    }

    // Call the JStyle plugin to start real-time measurement
    JStyle.getRealtimeMeasurementData({ type: measurementType })
      .then((result) => {
        console.log("Measurement started:", result);
        setStatus("Measurement in progress...");
      })
      .catch((error) => {
        console.error("Error starting measurement:", error);
        setStatus("Failed to start measurement");
        setIsMeasuring(false);
        setMeasurementData({
          value: "Error",
          unit: "",
          status: "Failed to start",
          timestamp: new Date().toLocaleTimeString(),
          rawData: error
        });
      });
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setIsMeasuring(false);
    setMeasurementData(null);
    setCurrentMetric('');
    setStatus('');
  };

  const heartMetrics = [
    {
      id: 'heartRate',
      name: 'Heart Rate',
      bengaliName: 'হৃদস্পন্দন হার',
      icon: <HeartRateIcon />,
      description: 'Monitor your heart rate throughout the day',
      color: 'bg-red-500',
      unit: 'BPM',
      currentValue: '72',
      canMeasure: true
    },
    {
      id: 'hrv',
      name: 'Heart Rate Variability',
      bengaliName: 'হৃদস্পন্দন পরিবর্তনশীলতা',
      icon: <HRVIcon />,
      description: 'Track your HRV for stress and recovery insights',
      color: 'bg-blue-500',
      unit: 'ms',
      currentValue: '45',
      canMeasure: true
    },
    {
      id: 'bloodOxygen',
      name: 'Blood Oxygen',
      bengaliName: 'রক্তে অক্সিজেন',
      icon: <BloodOxygenIcon />,
      description: 'Monitor your blood oxygen saturation levels',
      color: 'bg-green-500',
      unit: '%',
      currentValue: '98',
      canMeasure: true
    },
    // {
    //   id: 'vo2max',
    //   name: 'VO2 Max',
    //   bengaliName: 'ভিও২ ম্যাক্স',
    //   icon: <VO2MaxIcon />,
    //   description: 'Track your maximum oxygen consumption',
    //   color: 'bg-purple-500',
    //   unit: 'ml/kg/min',
    //   currentValue: '42',
    //   canMeasure: false
    // }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-14">
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">ফিরে যান</span>
          </button>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">হৃদয় স্বাস্থ্য</h1>
          <p className="text-gray-600">আপনার হৃদয় সম্পর্কিত স্বাস্থ্য মেট্রিক্স ট্র্যাক করুন</p>
        </div>

        {/* Platform Warning for Web */}
        {!Capacitor.isNativePlatform() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-yellow-800 font-medium">Web Platform</span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Device measurement features are not available on web platform. Use the mobile app for full functionality.
            </p>
          </div>
        )}

        {/* Heart Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {heartMetrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden group"
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">{metric.bengaliName}</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>
                
                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-gray-800">{metric.currentValue}</span>
                    <span className="text-gray-500 ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">বর্তমান মান</div>
                    <div className="text-xs text-green-600">+2.3%</div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">গড়</span>
                      <div className="font-medium text-gray-700">{metric.currentValue} {metric.unit}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">সর্বোচ্চ</span>
                      <div className="font-medium text-gray-700">{Math.round(parseInt(metric.currentValue) * 1.2)} {metric.unit}</div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCardClick(metric.id)}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      View Data
                    </button>
                    {metric.canMeasure && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMeasure(metric.id);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Measure
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>


      </div>

      {/* Measurement Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              {isMeasuring ? (
                <>
                  <div className="mb-4">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {currentMetric === 'heartRate' && 'হৃদস্পন্দন হার পরিমাপ করা হচ্ছে...'}
                    {currentMetric === 'hrv' && 'হৃদস্পন্দন পরিবর্তনশীলতা পরিমাপ করা হচ্ছে...'}
                    {currentMetric === 'bloodOxygen' && 'রক্তে অক্সিজেন পরিমাপ করা হচ্ছে...'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    {isConnected ? 'সংযোগ স্থাপিত হয়েছে' : 'সংযোগ স্থাপন করা হচ্ছে...'}
                    অনুগ্রহ করে অপেক্ষা করুন। এটি ১৫ সেকেন্ড সময় নিতে পারে।
                  </p>
                  {status && (
                    <p className="text-blue-600 text-sm font-medium">
                      {status}
                    </p>
                  )}
                </>
              ) : measurementData ? (
                <>
                  <div className="mb-4">
                    <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
                      measurementData.status === 'Normal' || measurementData.status === 'Good' 
                        ? 'bg-green-100' 
                        : 'bg-yellow-100'
                    }`}>
                      <svg className={`w-8 h-8 ${
                        measurementData.status === 'Normal' || measurementData.status === 'Good' 
                          ? 'text-green-600' 
                          : 'text-yellow-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {currentMetric === 'heartRate' && 'হৃদস্পন্দন হার পরিমাপ সম্পন্ন'}
                    {currentMetric === 'hrv' && 'হৃদস্পন্দন পরিবর্তনশীলতা পরিমাপ সম্পন্ন'}
                    {currentMetric === 'bloodOxygen' && 'রক্তে অক্সিজেন পরিমাপ সম্পন্ন'}
                  </h3>
                  <div className="mb-4">
                    {/* Display specific measured value based on currentMetric */}
                    <div className="text-center">
                      {currentMetric === 'heartRate' && measurementData.heart && (
                        <div className="p-4 bg-red-50 rounded-lg">
                          <div className="text-3xl font-bold text-red-600 mb-1">{measurementData.heart}</div>
                          <div className="text-gray-700 font-medium">BPM</div>
                        </div>
                      )}
                      {currentMetric === 'hrv' && measurementData.hrv && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <div className="text-3xl font-bold text-blue-600 mb-1">{measurementData.hrv}</div>
                          <div className="text-gray-700 font-medium">ms</div>
                        </div>
                      )}
                      {currentMetric === 'bloodOxygen' && measurementData.blood_oxygen && (
                        <div className="p-4 bg-green-50 rounded-lg">
                          <div className="text-3xl font-bold text-green-600 mb-1">{measurementData.blood_oxygen}</div>
                          <div className="text-gray-700 font-medium">%</div>
                        </div>
                      )}
                      {((currentMetric === 'heartRate' && !measurementData.heart) ||
                        (currentMetric === 'hrv' && !measurementData.hrv) ||
                        (currentMetric === 'bloodOxygen' && !measurementData.blood_oxygen)) && (
                        <div className="text-center text-gray-500">
                          কোন পরিমাপ পাওয়া যায়নি
                        </div>
                      )}
                    </div>
                    <div className={`text-sm font-medium mt-3 ${
                      measurementData.status === 'Success' || measurementData.status === 'Normal' || measurementData.status === 'Good' 
                        ? 'text-green-600' 
                        : 'text-yellow-600'
                    }`}>
                      {measurementData.status}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {measurementData.timestamp}
                    </div>
                  </div>
                  <button
                    onClick={closeDialog}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    বন্ধ করুন
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Heart; 